import React from 'react';
import { Stack, StackProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

// Polymorphic component types
type AsProp<C extends React.ElementType> = {
  as?: C;
};

type PropsToOmit<C extends React.ElementType, P> = keyof (AsProp<C> & P);

type PolymorphicComponentProp<
  C extends React.ElementType,
  Props = Record<string, never>,
> = React.PropsWithChildren<Props & AsProp<C>> &
  Omit<React.ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>;

type PolymorphicRef<C extends React.ElementType> = React.ComponentPropsWithRef<C>['ref'];

type PolymorphicComponentPropWithRef<
  C extends React.ElementType,
  Props = Record<string, never>,
> = PolymorphicComponentProp<C, Props> & { ref?: PolymorphicRef<C> };

// Base Flex props without the polymorphic part
interface BaseFlexProps
  extends Omit<StackProps, keyof PounceStyleProps | 'component'>, PounceStyleProps {
  /** An alias for `flexDirection` */
  direction?: StackProps['direction'];

  /** An alias for `justifyContent` */
  justify?: StackProps['justifyContent'];

  /** An alias for `alignItems` */
  align?: StackProps['alignItems'];

  /** An alias for `flexWrap` */
  wrap?: StackProps['flexWrap'];

  /** An alias for `flexBasis` style prop */
  basis?: string | number;

  /** An alias for `flexGrow` style prop */
  grow?: number;

  /** An alias for `flexShrink` style prop */
  shrink?: number;

  /** Whether the flex should be `flex-inline` */
  inline?: boolean;

  /** An alias for the `spacing` property, it defines the gap between the flexbox children */
  spacing?: StackProps['spacing'];

  /** Additional styles to apply (combined with pounce props) */
  sx?: StackProps['sx'];
}

// Polymorphic FlexProps that extends the props of whatever element is passed to "as"
export type FlexProps<C extends React.ElementType = 'div'> = PolymorphicComponentPropWithRef<
  C,
  BaseFlexProps
>;

/**
 * Flex component built on top of MUI Stack.
 *
 * Responsive flexbox layout component. You should use this anytime you want a flexbox layout. It provides
 * all the functionality from the original Pounce Flex while leveraging MUI Stack's spacing system
 * and responsive capabilities.
 *
 * Features:
 * - Full pounce Flex API compatibility
 * - Flexbox shorthand props (direction, justify, align, etc.)
 * - Built on top of MUI Stack component
 * - Theme-aware spacing system
 * - Responsive breakpoint support
 * - All pounce style props support
 * - Polymorphic: Accepts all props of the element passed to "as"
 */
export const Flex = React.forwardRef(function Flex<C extends React.ElementType = 'div'>(
  {
    direction = 'row',
    justify,
    align,
    inline,
    wrap,
    spacing,
    shrink,
    grow,
    basis,
    as = 'div' as C,
    sx,
    ...rest
  }: FlexProps<C>,
  ref?: PolymorphicRef<C>,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Combine pounce-generated styles with flex-specific styles and user sx
  const combinedSx: StackProps['sx'] = {
    ...pounceStyles,
    ...(inline && { display: 'inline-flex' }),
    ...(wrap && { flexWrap: wrap }),
    ...(shrink !== undefined && { flexShrink: shrink }),
    ...(grow !== undefined && { flexGrow: grow }),
    ...(basis && { flexBasis: basis }),
    ...(sx || {}),
  } as StackProps['sx'];

  return (
    <Stack
      ref={ref}
      component={as}
      direction={direction}
      justifyContent={justify}
      alignItems={align}
      spacing={spacing}
      sx={combinedSx}
      {...otherProps}
    />
  );
}) as <C extends React.ElementType = 'div'>(props: FlexProps<C>) => React.ReactElement | null;
